import { getProgressionSkillsForSport } from '../data/progression/skills';
import { getSkillTracksForSport } from '../data/progression/skillTracks';
import { PROGRESSION_LEVELS } from './progressionConstants';

function levelIndex(level) {
  return PROGRESSION_LEVELS.indexOf(level);
}

function isTrackUnlocked(track, currentLevel, unlockedTrackIds) {
  if (!track.branchFromLevel) return true;
  if (unlockedTrackIds.has(track.trackId)) return true;
  if (!currentLevel) return false;
  return levelIndex(currentLevel) >= levelIndex(track.branchFromLevel);
}

// Pass 78 — TRUTHFUL status vocabulary. Curriculum ORDER (a skill sitting earlier in a track) and a demonstrated
// LEVEL do NOT prove a student individually demonstrated every earlier skill, so the old ordering-/level-derived
// 'completed' status is removed (it fabricated historical achievement). Only these statuses are used:
//   · 'demonstrated'  — the exact canonical skill of a completed lesson whose outcome demonstrated it (Pass 77).
//   · 'current_focus' — the canonical evidence winner (what coaching evidence says to work on now).
//   · 'suggested_next'— the recommended next skill.
//   · 'locked'        — in a branch track not yet available (unknown/insufficient demonstrated level).
//   · 'available'     — browseable curriculum guidance (NOT a claim the student has done it).
function buildSkillStatus(skill, context) {
  const { trackUnlocked, currentSkillId, nextSkillId, demonstratedSkillIds } = context;

  if (skill.skillId === currentSkillId) return 'current_focus';
  if (skill.skillId === nextSkillId) return 'suggested_next';
  if (demonstratedSkillIds.includes(skill.skillId)) return 'demonstrated';
  if (!trackUnlocked) return 'locked';
  return 'available';
}

export function buildSkillRoadmapModel(sport, summary = {}) {
  if (!sport) {
    return {
      sport: '',
      currentLevel: '',
      activeTrackId: '',
      currentSkillId: '',
      nextSkillId: '',
      unlockedTrackIds: [],
      tracks: [],
      coreTrack: null,
      branchTracks: [],
    };
  }

  const tracks = getSkillTracksForSport(sport);
  const skills = getProgressionSkillsForSport(sport);
  const unlockedTrackIds = new Set(summary?.progressionRecommendation?.unlockedTrackIds || []);
  const currentLevel = summary?.currentLevel || '';
  const activeTrackId = summary?.activeTrack?.trackId || `${sport === 'skiing' ? 'ski' : 'snowboard'}_progression_core`;
  const currentSkillId = summary?.currentSkill?.skillId || '';
  const nextSkillId = summary?.nextSkill?.skillId || '';
  // Only skills a completed lesson actually DEMONSTRATED (Pass 77) may be shown as achieved — never inferred from order.
  const demonstratedSkillIds = (summary?.lessonSignals?.demonstratedSkills || [])
    .map((s) => s?.skillId)
    .filter(Boolean);

  const visibleTracks = tracks
    .map((track) => {
      const trackSkills = skills
        .filter((skill) => skill.trackId === track.trackId)
        .sort((a, b) => levelIndex(a.level) - levelIndex(b.level) || (a.order || 0) - (b.order || 0));

      if (trackSkills.length === 0) return null;

      const trackUnlocked = isTrackUnlocked(track, currentLevel, unlockedTrackIds);

      return {
        trackId: track.trackId,
        name: track.name,
        type: track.type,
        isUnlocked: trackUnlocked,
        isActiveTrack: track.trackId === activeTrackId,
        branchFromLevel: track.branchFromLevel || '',
        skills: trackSkills.map((skill) => ({
          skillId: skill.skillId,
          name: skill.name,
          level: skill.level,
          description: skill.description || '',
          status: buildSkillStatus(skill, {
            trackUnlocked,
            currentSkillId,
            nextSkillId,
            demonstratedSkillIds,
          }),
        })),
      };
    })
    .filter(Boolean);

  return {
    sport,
    currentLevel,
    activeTrackId,
    currentSkillId,
    nextSkillId,
    unlockedTrackIds: Array.from(unlockedTrackIds),
    tracks: visibleTracks,
    coreTrack: visibleTracks.find((track) => track.type === 'progression') || null,
    branchTracks: visibleTracks.filter((track) => track.type === 'branch'),
  };
}
